import { type FC, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import type { DashboardOverview } from "../types";

import {
  OrgApiKeysProvider,
  PROVIDER_LABELS,
  useOrgApiKeys,
} from "../context/OrgApiKeysContext";
import { useTheme } from "../context/ThemeContext";
import { useActorSystemEnabled } from "../hooks/useActorSystemEnabled";
import { useDashboardNavigation } from "../hooks/useDashboardNavigation";
import { setDesignSystemTheme } from "../theme/design-system";
import { getDashboardHeaderCopy } from "../utils/dashboardHeader";
import { AdminPanel } from "./admin/AdminPanel";
import { BlackboardPage } from "./blackboard/BlackboardPage";
import { TemplatesPage } from "./blackboard/TemplatesPage";
import { RepositoryChatPage } from "./chat";
import { Alert } from "./common/Alert";
import { DashboardHeader, type NavKey, Sidebar } from "./dashboard";
import { IntegrationEmblems } from "./dashboard/IntegrationEmblems";
import { TaskDetailsDrawer } from "./dashboard/TaskDetailsDrawer";
import { OverviewView } from "./dashboard/views/OverviewView";
import { ReleaseNotesView } from "./dashboard/views/ReleaseNotesView";
import { WikiView } from "./dashboard/views/WikiView";
import { IntegrationsPanel } from "./integrations";
import { HamburgerButton, MobileMenu } from "./MobileMenu";
import PlanSnapshotsView from "./planSnapshots/PlanSnapshotsView";
import { SettingsPanel } from "./settings";
import StatisticsView from "./statistics/StatisticsView";

const FULL_BLEED_VIEWS = new Set<NavKey>([
  "plans",
  "repo-chat",
  "agents-instances",
]);
// Views that need a flush-to-bottom layout (fixed viewport height + inner
// Flex column). Appropriate for chat-style pages with a bottom input that
// Must stay pinned. Grid pages should NOT be in here — they need normal
// Page scroll, especially on mobile where h-dvh + overflow-hidden on the
// Wrapper clips content past the initial viewport.
const FLUSH_BOTTOM_VIEWS = new Set<NavKey>(["repo-chat", "plans"]);

interface DashboardViewProps {
  data: DashboardOverview;
  onRefresh: () => void;
  loading: boolean;
  onLogout?: () => void;
  organizationId: string | null;
  realtime?: { status: string; isHealthy: boolean };
}

interface DashboardViewInnerProps {
  data: DashboardOverview;
  onRefresh: () => void;
  loading: boolean;
  onLogout?: () => void;
  realtime?: { status: string; isHealthy: boolean };
}

export const DashboardView: FC<DashboardViewProps> = ({
  organizationId,
  ...props
}) => (
  <OrgApiKeysProvider organizationId={organizationId}>
    <DashboardViewInner {...props} />
  </OrgApiKeysProvider>
);

const DashboardViewInner: FC<DashboardViewInnerProps> = ({
  data,
  onRefresh,
  loading,
  onLogout,
  realtime,
}) => {
  const { theme } = useTheme();
  const { missingProviders, loading: keysLoading } = useOrgApiKeys();
  const navigation = useDashboardNavigation();
  const nav = useNavigate();
  const handleNavigateToIndexing = useCallback(
    (repoSlug: string) => {
      void nav(
        `/integrations?tab=indexing&action=index&repo=${encodeURIComponent(repoSlug)}`,
      );
    },
    [nav],
  );
  const headerCopy = getDashboardHeaderCopy({
    activeView: navigation.activeView,
    generatedAt: data.generated_at,
  });
  useEffect(() => {
    setDesignSystemTheme(theme);
  }, [theme]);

  const sidebar = (
    <Sidebar
      activeView={navigation.activeView}
      onNavClick={navigation.handleNavClick}
    />
  );
  return (
    <div className="flex flex-col lg:h-[calc(100vh)] lg:flex-row lg:overflow-hidden">
      <MobileMenu
        isOpen={navigation.isMobileMenuOpen}
        onClose={navigation.closeMobileMenu}
      >
        {sidebar}
      </MobileMenu>
      <aside className="hidden h-full w-full max-w-xs flex-shrink-0 flex-col justify-between overflow-hidden border-r-2 border-slate-300 bg-slate-100 px-5 py-6 text-sm text-slate-600 transition-all duration-300 lg:flex dark:border-slate-700 dark:bg-arti-panel dark:text-slate-300">
        {sidebar}
      </aside>
      <main className="flex-1 overflow-y-auto">
        <div
          className={`mx-auto w-full max-w-7xl py-4 lg:py-6 ${FLUSH_BOTTOM_VIEWS.has(navigation.activeView) ? "flex h-dvh flex-col overflow-hidden pb-0 lg:h-full lg:pb-0" : ""}`}
        >
          <div className="flex flex-col gap-3 px-3 lg:gap-5 lg:px-6">
            <DashboardMobileHeader
              kicker={headerCopy.kicker}
              isMobileMenuOpen={navigation.isMobileMenuOpen}
              onToggle={navigation.toggleMobileMenu}
            />
            <DashboardHeader
              kicker={headerCopy.kicker}
              title={headerCopy.title}
              subtitle={headerCopy.subtitle}
              onRefresh={onRefresh}
              onLogout={onLogout}
              loading={loading}
              realtime={realtime}
              showActions={navigation.activeView === "overview"}
              titleAdornment={
                navigation.activeView === "overview" ? (
                  <IntegrationEmblems />
                ) : undefined
              }
            />
          </div>
          {missingProviders.length > 0 && !keysLoading && (
            <div className="mt-3 px-3 lg:mt-5 lg:px-6">
              <Alert
                variant="warning"
                action={
                  <Link
                    to="/settings?tab=api-keys"
                    className="whitespace-nowrap font-medium underline"
                  >
                    View details
                  </Link>
                }
              >
                Your organization is missing required AI provider keys (
                {missingProviders
                  .map((p) => PROVIDER_LABELS[p] ?? p)
                  .join(", ")}
                ). Contact your platform administrator to provision them.
              </Alert>
            </div>
          )}
          <div
            className={`mt-3 flex flex-col gap-3 lg:mt-5 lg:gap-5 ${FULL_BLEED_VIEWS.has(navigation.activeView) ? "" : "px-3 lg:px-6"} ${FLUSH_BOTTOM_VIEWS.has(navigation.activeView) ? "min-h-0 flex-1" : ""}`}
          >
            <DashboardContent
              activeView={navigation.activeView}
              data={data}
              onRefresh={onRefresh}
              onNavigateToIndexing={handleNavigateToIndexing}
            />
          </div>
        </div>
      </main>
      <TaskDetailsDrawer />
    </div>
  );
};

interface DashboardContentProps {
  activeView: NavKey;
  data: DashboardOverview;
  onRefresh: () => void;
  onNavigateToIndexing?: (repoSlug: string) => void;
}

const DashboardContent: FC<DashboardContentProps> = ({
  activeView,
  data,
  onRefresh,
  onNavigateToIndexing,
}) => {
  const actorsEnabled = useActorSystemEnabled().enabled;
  switch (activeView) {
    case "overview": {
      return (
        <OverviewView
          data={data}
          onRefresh={onRefresh}
          onNavigateToIndexing={onNavigateToIndexing}
        />
      );
    }
    case "plans": {
      return <PlanSnapshotsView />;
    }
    case "agents-instances": {
      return actorsEnabled ? <BlackboardPage /> : null;
    }
    case "agents-templates": {
      return actorsEnabled ? <TemplatesPage /> : null;
    }
    case "repo-chat": {
      return <RepositoryChatPage />;
    }
    case "integrations": {
      return <IntegrationsPanel />;
    }
    case "settings": {
      return <SettingsPanel />;
    }
    case "usage": {
      return <StatisticsView />;
    }
    case "admin": {
      return <AdminPanel />;
    }
    case "wiki": {
      return <WikiView />;
    }
    case "release-notes": {
      return <ReleaseNotesView />;
    }
    default: {
      return null;
    }
  }
};

interface DashboardMobileHeaderProps {
  kicker: string;
  isMobileMenuOpen: boolean;
  onToggle: () => void;
}

const DashboardMobileHeader: FC<DashboardMobileHeaderProps> = ({
  kicker,
  isMobileMenuOpen,
  onToggle,
}) => (
  <div className="flex items-center justify-between gap-4 lg:hidden">
    <p className="text-sm uppercase tracking-wide text-gray-400 dark:text-white/50">
      {kicker}
    </p>
    <HamburgerButton isOpen={isMobileMenuOpen} onClick={onToggle} />
  </div>
);

export default DashboardView;
